// src/components/admin/AdminCollegeForm.jsx
import React, { useState, useEffect } from 'react';

const AdminCollegeForm = ({ college, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    logoUrl: '' // Empty string represents no logo (completely optional)
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (college) {
      setFormData({
        name: college.name || '',
        code: college.code || '',
        logoUrl: college.logoUrl || '' // Fallback to empty string if database returns null
      });
    }
  }, [college]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Strict validation only on required structural fields
    if (!formData.name || !formData.code) {
      alert('Name and Code are required fields.');
      return;
    }
    
    setLoading(true);
    try {
      // Normalize data: if logoUrl is blank or deleted, save it as clean null
      const payload = {
        ...formData,
        logoUrl: formData.logoUrl.trim() === '' ? null : formData.logoUrl.trim()
      };
      
      await onSave(payload);
    } catch (error) {
      console.error('Error saving college:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white p-6 rounded-xl shadow-md border border-gray-100 max-w-xl mx-auto">
      <h3 className="text-xl font-bold text-gray-800 mb-4">
        {college ? 'Edit Institution' : 'Add New Institution'}
      </h3>
      
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* College Name Input */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">College Name</label>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder="e.g., IIT Delhi"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition"
          />
        </div>

        {/* College Code Input */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">College Code</label>
          <input
            type="text"
            name="code"
            value={formData.code}
            onChange={handleChange}
            placeholder="e.g., IITD"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition"
          />
        </div>

        {/* Optional Logo URL Input */}
        <div>
          <div className="flex justify-between items-center mb-1">
            <label className="block text-sm font-medium text-gray-700">Logo Image URL</label>
            <span className="text-xs text-gray-400 italic">Optional</span>
          </div>
          <input
            type="text" // Keeps browser validation from hijacking submission
            name="logoUrl"
            value={formData.logoUrl}
            onChange={handleChange}
            placeholder="https://example.com/logo.png (Leave blank if none)"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition"
          />
        </div>

        {/* Conditional Logo Preview */}
        {formData.logoUrl.trim() !== '' && (
          <div className="mt-2 p-2 border border-gray-100 rounded-lg bg-gray-50 flex items-center gap-3">
            <span className="text-xs text-gray-500">Logo Preview:</span>
            <img 
              src={formData.logoUrl} 
              alt="Preview" 
              className="h-10 w-10 object-contain bg-white rounded p-1 border"
              onError={(e) => { e.target.src = 'https://placehold.co/100?text=Invalid+URL'; }}
            />
          </div>
        )}

        {/* Form Action Buttons */}
        <div className="flex justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition font-medium"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition font-medium disabled:opacity-50"
          >
            {loading ? 'Saving...' : college ? 'Update College' : 'Add College'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AdminCollegeForm;